import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { FieldLabel } from '@/components/ui/InfoTip';
import { cn } from '@/lib/cn';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  /** Shown behind an ⓘ beside the label: what the field means and what to enter. */
  info?: ReactNode;
}

/**
 * Labelled input with inline validation messaging.
 *
 * The error is wired through `aria-describedby` and announced with `role=alert`,
 * so it is not a visual-only signal.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, info, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  return (
    <div className="w-full">
      <FieldLabel htmlFor={fieldId} label={label} info={info} />
      <input
        id={fieldId}
        ref={ref}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className={cn(
          'h-11 w-full rounded-xl border bg-white px-3.5 text-sm text-ink',
          'placeholder:text-muted focus:outline-none focus:ring-2',
          error
            ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
            : 'border-line focus:border-brand-400 focus:ring-brand-100',
          className,
        )}
        {...props}
      />
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-sm font-medium text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
